import { Singleton } from 'typescript-ioc/es5'
import aqe from '@pushly/aqe'
import { HttpMethod } from '@pushly/aqe/lib/enums'
import { IApiCallOptions, IApiDeleteResponse, IApiResponse } from '@pushly/aqe/lib/interfaces'
import { AbstractRequestService } from '@pushly/aqe/lib/services/abstract-request-service'
import { AiGenerationDirective } from '@pushly/models/lib/structs/pushly-ai/ai-generation-directive'
import { CompiledDirective } from '@pushly/models/lib/structs/pushly-ai/compiled-directive'
import { CreateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/create-ai-generation-directive-request'
import { UpdateAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/update-ai-generation-directive-request'
import { CompileAiGenerationDirectiveRequest } from '@pushly/models/lib/structs/pushly-ai/compile-ai-generation-directive-request'
import { CompileAiGenerationDirectiveResponse } from '@pushly/models/lib/structs/pushly-ai/compile-ai-generation-directive-response'
import { axiosFetch } from '../config/axios-setup'
import { extractResponseErrorMessageOrNull } from '../_utils/response-error-utils'
import { SuggestDirectivePromotionsRequest } from '@pushly/models/lib/structs/pushly-ai/suggest-directive-promotions-request'
import { SuggestDirectivePromotionsResponse } from '@pushly/models/lib/structs/pushly-ai/suggest-directive-promotions-response'

/**
 * How a Brand Voice save ended. `stale`: the preview it named expired, was for other text, or the
 * Brand Voice changed since (someone else saved); nothing was written. `failed`: anything else;
 * nothing was written either, and `message` is the server's own, when it gave one.
 */
export type BrandVoiceSaveResult =
    | { kind: 'saved'; directive: AiGenerationDirective }
    | { kind: 'stale' }
    | { kind: 'failed'; message: string | null }

/**
 * Client for the sailfish AI generation directive CRUD:
 *   GET   /domains/:domainId/ai-generation-directives[?role=]
 *   POST  /domains/:domainId/ai-generation-directives
 *   PATCH /domains/:domainId/ai-generation-directives/:directiveId
 *   DELETE /domains/:domainId/ai-generation-directives/:directiveId  (soft-delete)
 *
 * The sailfish controller runs with its payload case-converter disabled and
 * validates the snake-case `@pushly/models` structs, so this service uses those
 * structs directly with `skipCaseConversion` (no camelCase bridging).
 */
@Singleton
export class AiGenerationDirectiveService extends AbstractRequestService {
    public constructor() {
        super()
    }

    /** Resolve the domain's Brand Voice directive (the singleton `brand_voice`), or null. */
    public async fetchBrandVoice(
        domainId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective | null>> {
        const res = await this.request(HttpMethod.GET, `${this.getEndpoint(domainId)}?role=brand_voice`, {
            ...options,
            config: { skipCaseConversion: true },
            responseType: [AiGenerationDirective],
        })

        return { ...res, data: res.data?.[0] ?? null }
    }

    public async create(
        domainId: number,
        dto: CreateAiGenerationDirectiveRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.POST, this.getEndpoint(domainId), {
            ...options,
            data: dto,
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    public async update(
        domainId: number,
        directiveId: number,
        dto: UpdateAiGenerationDirectiveRequest,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<AiGenerationDirective>> {
        return this.request(HttpMethod.PATCH, `${this.getEndpoint(domainId)}/${directiveId}`, {
            ...options,
            data: dto,
            config: { skipCaseConversion: true },
            responseType: AiGenerationDirective,
        })
    }

    /**
     * Remove a directive. The server soft-deletes it (and its versions), so generation stops
     * resolving it, and a new directive of the same role can be created afterwards.
     */
    public async destroy(
        domainId: number,
        directiveId: number,
        options: IApiCallOptions = {},
    ): Promise<IApiDeleteResponse> {
        return this.requestDelete(`${this.getEndpoint(domainId)}/${directiveId}`, options)
    }

    /**
     * Compile a free-text directive body into its decomposition for review, without persisting:
     * the tiered `compiled_rules` (the guaranteed `spec`, judge-checked `assertions`, best-effort
     * `style` and `unmapped` residue), and the `preview_id` a save sends back to store exactly
     * those rules with the new version.
     */
    public async compilePreview(
        domainId: number,
        body: string,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<CompileAiGenerationDirectiveResponse>> {
        return this.request(HttpMethod.POST, `${this.getEndpoint(domainId)}/compile-preview`, {
            ...options,
            data: CompileAiGenerationDirectiveRequest.from({ body }),
            config: { skipCaseConversion: true },
            responseType: CompileAiGenerationDirectiveResponse,
        })
    }

    /**
     * Save the Brand Voice's text together with the rules the author reviewed for it, in one write:
     * creates it when `directiveId` is null, otherwise adds a new version. `previewId` names the
     * compile preview of exactly this `body`.
     *
     * Not routed through `request`, which shows its own toast for every error: a 409 here is an
     * expected outcome the editor handles itself, by refreshing the preview.
     */
    public async saveBrandVoice(
        domainId: number,
        directiveId: number | null,
        body: string,
        previewId: string,
    ): Promise<BrandVoiceSaveResult> {
        const endpoint = this.getEndpoint(domainId)
        try {
            const res = directiveId
                ? await axiosFetch('patch', {
                      url: `${endpoint}/${directiveId}`,
                      data: UpdateAiGenerationDirectiveRequest.from({ body, preview_id: previewId }).serialize(),
                      config: { skipCaseConversion: true } as any,
                  })
                : await axiosFetch('post', {
                      url: endpoint,
                      data: CreateAiGenerationDirectiveRequest.from({
                          role: 'brand_voice',
                          name: 'Brand Voice',
                          body,
                          preview_id: previewId,
                      }).serialize(),
                      config: { skipCaseConversion: true } as any,
                  })

            return { kind: 'saved', directive: AiGenerationDirective.from(res.data.data) }
        } catch (error: any) {
            if (error?.response?.status === 409) return { kind: 'stale' }
            const status: number | undefined = error?.response?.status
            return { kind: 'failed', message: status && status < 500 ? extractResponseErrorMessageOrNull(error) : null }
        }
    }

    /**
     * Ask which prose-tier statements in a compile are one missing operand away from being
     * enforceable, and for the rewrite that would close the gap.
     *
     * The compile is sent UP rather than referenced by id because the drawer routinely displays
     * a preview that was never persisted. Nothing is written by this call.
     *
     * Purely additive to the editor: a failure here costs the author a hint, not their work, so
     * callers are expected to ignore a bad response rather than surface an error.
     */
    public async suggestPromotions(
        domainId: number,
        compiledRules: CompiledDirective,
        options: IApiCallOptions = {},
    ): Promise<IApiResponse<SuggestDirectivePromotionsResponse>> {
        return this.request(HttpMethod.POST, `${this.getEndpoint(domainId)}/suggest-promotions`, {
            ...options,
            data: SuggestDirectivePromotionsRequest.from({ compiled_rules: compiledRules }),
            config: { skipCaseConversion: true },
            responseType: SuggestDirectivePromotionsResponse,
        })
    }

    private getEndpoint(domainId: number): string {
        return `${aqe.defaults.publicApiDomain}/domains/${domainId}/ai-generation-directives`
    }
}
